import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Navbar, Footer } from '../components/Layout';
import EmailSignupForm from '../components/growth/EmailSignupForm';
import { Search, Clock, Star, ArrowRight, Flame } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '');

export const PostCard = ({ post, size = 'md' }) => (
  <Link to={`/insights/${post.slug}`} data-testid={`post-card-${post.slug}`}
    className="group block rounded-2xl border border-slate-700/70 bg-slate-900/40 overflow-hidden hover:border-cyan-500/40 transition-colors duration-200">
    {post.featured_image && (
      <div className={size === 'lg' ? 'h-56 overflow-hidden' : 'h-40 overflow-hidden'}>
        <img src={post.featured_image} alt={post.featured_image_alt || post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
    )}
    <div className="p-5">
      <span className="text-[11px] uppercase tracking-wider text-cyan-300 font-semibold">{post.category}</span>
      <h3 className={`font-outfit font-semibold text-white mt-1.5 group-hover:text-cyan-200 transition-colors duration-200 ${size === 'lg' ? 'text-2xl' : 'text-lg'}`}>
        {post.title}
      </h3>
      {post.summary && <p className="text-sm text-slate-300 mt-2 line-clamp-3">{post.summary}</p>}
      <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-300">
        <span>{post.author_name}</span>
        <span>{fmt(post.published_at)}</span>
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{post.reading_time} min</span>
      </div>
    </div>
  </Link>
);

export default function InsightsPage() {
  const { categorySlug } = useParams();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const [search, setSearch] = useState(q);
  const [data, setData] = useState({ posts: [], total: 0 });
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [meta, setMeta] = useState({ categories: [], tags: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, feat, pop, m] = await Promise.all([
        axios.get(`${API}/blog/posts`, { params: { category: categorySlug, q: q || undefined, limit: 24 } }),
        axios.get(`${API}/blog/posts`, { params: { featured: true, limit: 1 } }),
        axios.get(`${API}/blog/popular`, { params: { limit: 5 } }),
        axios.get(`${API}/blog/meta`),
      ]);
      setData(list.data);
      setFeatured(feat.data.posts || []);
      setPopular(pop.data.posts || []);
      setMeta(m.data);
    } finally {
      setLoading(false);
    }
  }, [categorySlug, q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const cat = meta.categories?.find((c) => c.slug === categorySlug);
    document.title = cat ? `${cat.name} | FuturePath Insights` : 'FuturePath Insights | Agentic FuturePath AI';
  }, [categorySlug, meta]);

  const activeCategory = meta.categories?.find((c) => c.slug === categorySlug);

  return (
    <div className="min-h-screen bg-navy-gradient">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <p className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-semibold">FuturePath Insights</p>
        <h1 className="font-outfit text-4xl sm:text-5xl font-bold tracking-tight text-white mt-2">
          {activeCategory ? activeCategory.name : 'Guidance for the path ahead'}
        </h1>
        <p className="text-base text-slate-300 mt-3 max-w-2xl">
          Practical, honest writing on careers, college, skilled trades, scholarships, money and how AI is
          reshaping the work your kids will actually do.
        </p>

        <div className="flex flex-col lg:flex-row gap-4 mt-8">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-300 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setParams(search ? { q: search } : {})}
              placeholder="Search articles…" data-testid="insights-search"
              className="w-full bg-slate-900/70 border border-slate-700 rounded-full pl-10 pr-4 py-2.5 text-sm text-white" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4" data-testid="insights-categories">
          <Link to="/insights" className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${!categorySlug ? 'border-cyan-400/50 bg-cyan-500/10 text-cyan-200' : 'border-slate-700 text-slate-300 hover:text-white'}`}>
            All
          </Link>
          {(meta.categories || []).filter((c) => c.count > 0 || c.slug === categorySlug).map((c) => (
            <Link key={c.slug} to={`/insights/category/${c.slug}`} data-testid={`category-${c.slug}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${categorySlug === c.slug ? 'border-cyan-400/50 bg-cyan-500/10 text-cyan-200' : 'border-slate-700 text-slate-300 hover:text-white'}`}>
              {c.name} <span className="text-slate-300">{c.count}</span>
            </Link>
          ))}
        </div>

        {!categorySlug && !q && featured.length > 0 && (
          <div className="mt-10" data-testid="insights-featured">
            <p className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold inline-flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5" /> Featured
            </p>
            <div className="mt-3"><PostCard post={featured[0]} size="lg" /></div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
          <div className="lg:col-span-2">
            <h2 className="text-base md:text-lg font-semibold text-white">
              {q ? `Results for "${q}"` : 'Recent articles'}
            </h2>
            {loading && <p className="text-sm text-slate-300 mt-4">Loading…</p>}
            {!loading && data.posts.length === 0 && (
              <div className="mt-6 rounded-2xl border border-slate-700/70 bg-slate-900/40 p-8 text-center" data-testid="insights-empty">
                <p className="text-white font-medium">No articles here yet.</p>
                <p className="text-sm text-slate-300 mt-1">
                  New guides are published regularly, join the waitlist and we'll send them to you.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5" data-testid="insights-grid">
              {data.posts.map((p) => <PostCard key={p.slug} post={p} />)}
            </div>
          </div>

          <aside className="space-y-6">
            <EmailSignupForm variant="full" source="blog_sidebar"
              heading="Get new guides by email"
              sub="One email when something genuinely useful goes live." />

            {popular.length > 0 && (
              <div className="rounded-2xl border border-slate-700/70 bg-slate-900/40 p-5" data-testid="insights-popular">
                <p className="text-[11px] uppercase tracking-wider text-slate-300 font-semibold inline-flex items-center gap-1.5">
                  <Flame className="w-3.5 h-3.5 text-amber-300" /> Most read
                </p>
                <ul className="mt-3 space-y-2.5">
                  {popular.map((p) => (
                    <li key={p.slug}>
                      <Link to={`/insights/${p.slug}`} className="text-sm text-slate-300 hover:text-cyan-300 transition-colors duration-200">
                        {p.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(meta.tags || []).length > 0 && (
              <div className="rounded-2xl border border-slate-700/70 bg-slate-900/40 p-5">
                <p className="text-[11px] uppercase tracking-wider text-slate-300 font-semibold">Topics</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {meta.tags.map((t) => (
                    <Link key={t.name} to={`/insights?q=${encodeURIComponent(t.name)}`}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-slate-700 text-slate-300 hover:text-cyan-300 hover:border-cyan-500/40 transition-colors duration-200">
                      {t.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <Link to="/waitlist" className="block rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-5 hover:bg-cyan-500/10 transition-colors duration-200">
              <p className="text-sm font-semibold text-white">Want the platform itself?</p>
              <p className="text-xs text-slate-300 mt-1">Join the early-access waitlist.</p>
              <span className="text-xs text-cyan-300 inline-flex items-center gap-1 mt-2">
                Join the waitlist <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
